import React, { useState, useEffect, useCallback } from 'react';
import { Save, Globe, Loader2, X, Trash2, AlertTriangle, Settings, Download, CheckCircle2, Workflow, RotateCw, Cpu, Users, Key, BookOpen } from 'lucide-react';
import baseapi from '../../services/baseapi';
import integrationapi from '../../services/integrationapi';
import { showError, showSuccess } from '../../utils/toast';
import { hasPermission } from '../../utils/permissionsHelper';
import { useClientLevel } from '../../hooks/useClientLevel';
import ExternalApiModal from './Integrations/ExternalApiModal';
import ExternalApiSettings from './Integrations/ExternalApiSettings';
import WebhookIntegrationModal from './Integrations/WebhookIntegrationModal';
import EmailIntegrationModal from './Integrations/EmailIntegrationModal';
import WhatsappIntegrationModal from './Integrations/WhatsappIntegrationModal';
import ManageClientsModal from './Integrations/ManageClientsModal';
import IntegrationDocumentationModal from './Integrations/IntegrationDocumentationModal';

export default function ApiIntegration() {
    const level = useClientLevel();
    const [integrations, setIntegrations] = useState<any[]>([]);
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isExternalApiModalOpen, setIsExternalApiModalOpen] = useState(false);
    const [isExternalApiSettingsOpen, setIsExternalApiSettingsOpen] = useState(false);
    const [isExternalApiDeleteModalOpen, setIsExternalApiDeleteModalOpen] = useState(false);
    const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);
    const [isWebhookDeleteModalOpen, setIsWebhookDeleteModalOpen] = useState(false);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [isEmailDeleteModalOpen, setIsEmailDeleteModalOpen] = useState(false);
    const [isWhatsappModalOpen, setIsWhatsappModalOpen] = useState(false);
    const [isWhatsappDeleteModalOpen, setIsWhatsappDeleteModalOpen] = useState(false);
    const [isManageClientsOpen, setIsManageClientsOpen] = useState(false);
    const [selectedIntegration, setSelectedIntegration] = useState<any>(null);

    const handleOpenManageClients = (integration: any) => {
        setSelectedIntegration(integration);
        setIsManageClientsOpen(true);
    };

    const canCreate = hasPermission('integrations', 'create')
    const canDelete = hasPermission('integrations', 'delete')
    const canUpdate = hasPermission('integrations', 'update')

    const [formData, setFormData] = useState({
        apiKey: '',
        apiUrl: '',
        apiSecret: '',
        targetDomain: ''
    });
    const [authMethod, setAuthMethod] = useState<'manual' | 'oauth'>('manual');

    const fetchIntegrations = useCallback(async () => {
        try {
            setLoading(true);
            const params: any = {};
            if (level && level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await integrationapi.get("/integrations", { params });
            if (response.data.status) {
                setIntegrations(response.data.data || []);
            }
        } catch (error) {
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    }, [level]);

    useEffect(() => {
        fetchIntegrations();
    }, [fetchIntegrations]);

    const existingDeskproRaw = integrations.find(item => item.integration_type === 'rundesk');
    const existingDeskpro = existingDeskproRaw ? {
        ...existingDeskproRaw,
        parsedData: typeof existingDeskproRaw.integration_data === 'string'
            ? JSON.parse(existingDeskproRaw.integration_data)
            : existingDeskproRaw.integration_data
    } : null;

    const isIntegrationEditable = useCallback((item: any) => {
        if (!item) return canUpdate;
        if (item.editable === false || item.editable === 0) return false;
        if (item.scope === 'global' && level && level.name !== 'global') return false;
        return canUpdate;
    }, [level, canUpdate]);

    const externalApiIntegration = integrations.find(item =>
        item.integration_type === 'external_api' ||
        item.integration_type === 'external_api_key'
    );

    const existingTeams = integrations.find(item =>
        item.integration_type === 'teams' ||
        item.integration_type === 'microsoft_teams'
    );

    const existingSlack = integrations.find(item =>
        item.integration_type === 'slack'
    );

    const existingSso = integrations.find(item =>
        item.integration_type === 'microsoft-sso' ||
        item.integration_type === 'microsoft_sso' ||
        item.integration_type === 'sso'
    );

    const existingGoogleWorkspace = integrations.find(item =>
        item.integration_type === 'google-workspace' ||
        item.integration_type === 'google_workspace' ||
        item.integration_type === 'google'
    );

    const existingGoogleSso = integrations.find(item =>
        item.integration_type === 'google-sso' ||
        item.integration_type === 'google_sso'
    );

    const existingWebhook = integrations.find(item => item.integration_type === 'webhook');
    const existingEmail = integrations.find(item => item.integration_type === 'smtp');
    const existingWhatsapp = integrations.find(item => item.integration_type === 'whatsapp' || item.integration_type === 'whatsapp_cloud');

    const handleInstall = async (provider: 'teams' | 'slack' | 'microsoft-sso' | 'google-sso' | 'google-workspace') => {
        try {
            const displayName =
                provider === 'teams' ? 'Microsoft Teams' :
                    provider === 'slack' ? 'Slack' :
                        provider === 'microsoft-sso' ? 'Microsoft SSO' :
                            provider === 'google-sso' ? 'Google SSO' : 'Google Workspace';

            showSuccess(`Initiating ${displayName} authorization...`);
            const params: any = {};
            if (level && level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await integrationapi.get(`integrations/${provider}/admin-consent-url`, { params });
            if (response.data.status && response.data.admin_consent_url) {
                window.location.href = response.data.admin_consent_url;
            } else {
                showError(`Failed to get consent URL for ${displayName}`);
            }
        } catch (error: any) {
            showError(error.response?.data?.message || `Failed to initiate ${provider} installation`);
        }
    };

    const handleUninstall = async (provider: 'teams' | 'slack' | 'microsoft-sso' | 'google-sso' | 'google-workspace', integrationId: any) => {
        try {
            setSubmitting(true);
            const displayName =
                provider === 'teams' ? 'Microsoft Teams' :
                    provider === 'slack' ? 'Slack' :
                        provider === 'microsoft-sso' ? 'Microsoft SSO' :
                            provider === 'google-sso' ? 'Google SSO' : 'Google Workspace';

            const params: any = {};
            if (level && level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await integrationapi.delete(`integrations/${provider}/uninstall`, { params });
            if (response.data.status) {
                showSuccess(`${displayName} disconnected successfully`);
                fetchIntegrations();
            } else {
                showError(`Failed to disconnect ${displayName}`);
            }
        } catch (error: any) {
            console.error("Uninstall error:", error);
            try {
                const params: any = {};
                if (level && level.name !== 'global' && level.id) {
                    params.client_id = level.id;
                }
                const response = await integrationapi.delete(`/integrations/${integrationId}`, { params });
                if (response.data.status) {
                    const displayName =
                        provider === 'teams' ? 'Microsoft Teams' :
                            provider === 'slack' ? 'Slack' :
                                provider === 'microsoft-sso' ? 'Microsoft SSO' :
                                    provider === 'google-sso' ? 'Google SSO' : 'Google Workspace';
                    showSuccess(`${displayName} disconnected successfully`);
                    fetchIntegrations();
                    return;
                }
            } catch (e) { }
            showError(error.response?.data?.message || `Failed to remove ${provider} integration`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenEdit = () => {
        if (existingDeskpro) {
            setFormData({
                apiUrl: existingDeskpro.parsedData.api_url || '',
                apiKey: existingDeskpro.parsedData.api_key || '',
                apiSecret: existingDeskpro.parsedData.api_secret || '',
                targetDomain: existingDeskpro.parsedData.target_domain || ''
            });
            setAuthMethod(existingDeskpro.parsedData?.auth_method || 'manual');
            setIsEditing(true);
            setIsConfigModalOpen(true);
        }
    };

    const [isDocModalOpen, setIsDocModalOpen] = useState(false);
    const [selectedDocId, setSelectedDocId] = useState<string | null>(null);

    const handleOpenDocs = (docId: string) => {
        setSelectedDocId(docId);
        setIsDocModalOpen(true);
    };

    const handleConfigureFromDocs = (docId: string) => {
        setIsDocModalOpen(false);
        switch (docId) {
            case 'rundeskpro':
                if (existingDeskpro) {
                    handleOpenEdit();
                } else if (canCreate) {
                    setFormData({ apiUrl: '', apiKey: '', apiSecret: '', targetDomain: '' });
                    setIsEditing(false);
                    setIsConfigModalOpen(true);
                }
                break;
            case 'external_api':
                if (externalApiIntegration) {
                    setIsExternalApiSettingsOpen(true);
                } else if (canCreate) {
                    setIsExternalApiModalOpen(true);
                }
                break;
            case 'teams':
                if (!existingTeams && canCreate) {
                    handleInstall('teams');
                }
                break;
            case 'microsoft-sso':
                if (!existingSso && canCreate) {
                    handleInstall('microsoft-sso');
                }
                break;
            case 'google-sso':
                if (!existingGoogleSso && canCreate) {
                    handleInstall('google-sso');
                }
                break;
            case 'slack':
                if (!existingSlack && canCreate) {
                    handleInstall('slack');
                }
                break;
            case 'google-workspace':
                if (!existingGoogleWorkspace && canCreate) {
                    handleInstall('google-workspace');
                }
                break;
            case 'webhook':
                setIsWebhookModalOpen(true);
                break;
            case 'smtp':
            case 'email':
                setIsEmailModalOpen(true);
                break;
            case 'whatsapp':
                setIsWhatsappModalOpen(true);
                break;
            default:
                break;
        }
    };

    const handleDelete = async () => {
        if (!existingDeskpro) return;
        try {
            setSubmitting(true);
            const params: any = {};
            if (level && level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await integrationapi.delete('integrations/rundesk/uninstall', { params });
            if (response.data.status) {
                showSuccess("Integration disconnected successfully");
                setIsDeleteModalOpen(false);
                fetchIntegrations();
            } else {
                showError("Failed to remove integration");
            }
        } catch (error) {
            console.error("Connect integrationapi delete failed, trying fallback...", error);
            try {
                const params: any = {};
                if (level && level.name !== 'global' && level.id) {
                    params.client_id = level.id;
                }
                const response = await baseapi.delete('/ticketing/integration', { params });
                if (response.data.status) {
                    showSuccess("Integration disconnected successfully");
                    setIsDeleteModalOpen(false);
                    fetchIntegrations();
                    return;
                }
            } catch (fallbackError) {
                console.error("Fallback delete failed:", fallbackError);
            }
            showError("Failed to remove integration");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteExternalApi = async () => {
        try {
            setSubmitting(true);
            const params: any = {};
            if (level && level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await integrationapi.delete("integrations/external-api/uninstall", { params });
            if (response.data.status) {
                showSuccess("External API disconnected successfully");
                setIsExternalApiDeleteModalOpen(false);
                fetchIntegrations();
            }
        } catch (error) {
            showError("Failed to remove external API");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteEmail = async () => {
        if (!existingEmail) return;
        try {
            setSubmitting(true);
            const params: any = {};
            if (level && level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await integrationapi.delete('integrations/smtp/disconnect', { params });
            if (response.data.status) {
                showSuccess("Email disconnected successfully");
                setIsEmailDeleteModalOpen(false);
                fetchIntegrations();
            } else {
                showError("Failed to remove Email");
            }
        } catch (error) {
            console.error("Email disconnect failed via integrationapi, trying fallback...", error);
            try {
                const params: any = {};
                if (level && level.name !== 'global' && level.id) {
                    params.client_id = level.id;
                }
                const response = await integrationapi.delete(`/integrations/${existingEmail.id}`, { params });
                if (response.data.status) {
                    showSuccess("Email disconnected successfully");
                    setIsEmailDeleteModalOpen(false);
                    fetchIntegrations();
                    return;
                }
            } catch (fallbackError) {
                console.error("Fallback delete failed:", fallbackError);
            }
            showError("Failed to remove Email");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteWebhook = async () => {
        if (!existingWebhook) return;
        try {
            setSubmitting(true);
            const params: any = {};
            if (level && level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await integrationapi.delete('integrations/webhook/disconnect', { params });
            if (response.data.status) {
                showSuccess("Webhook disconnected successfully");
                setIsWebhookDeleteModalOpen(false);
                fetchIntegrations();
            } else {
                showError("Failed to remove Webhook");
            }
        } catch (error) {
            console.error("Webhook disconnect failed via integrationapi, trying fallback...", error);
            try {
                const params: any = {};
                if (level && level.name !== 'global' && level.id) {
                    params.client_id = level.id;
                }
                const response = await integrationapi.delete(`/integrations/${existingWebhook.id}`, { params });
                if (response.data.status) {
                    showSuccess("Webhook disconnected successfully");
                    setIsWebhookDeleteModalOpen(false);
                    fetchIntegrations();
                    return;
                }
            } catch (fallbackError) {
                console.error("Fallback delete failed:", fallbackError);
            }
            showError("Failed to remove Webhook");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteWhatsapp = async () => {
        if (!existingWhatsapp) return;
        try {
            setSubmitting(true);
            const params: any = {};
            if (level && level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await integrationapi.delete('integrations/whatsapp', { params });
            if (response.data?.status || response.data?.success) {
                showSuccess("WhatsApp disconnected successfully");
                setIsWhatsappDeleteModalOpen(false);
                fetchIntegrations();
            } else {
                showError("Failed to remove WhatsApp");
            }
        } catch (error: any) {
            console.error("WhatsApp disconnect failed via integrationapi, trying fallback...", error);
            try {
                const params: any = {};
                if (level && level.name !== 'global' && level.id) {
                    params.client_id = level.id;
                }
                const response = await integrationapi.delete(`/integrations/${existingWhatsapp.id}`, { params });
                if (response.data?.status || response.data?.success) {
                    showSuccess("WhatsApp disconnected successfully");
                    setIsWhatsappDeleteModalOpen(false);
                    fetchIntegrations();
                    return;
                }
            } catch (fallbackError) {
                console.error("Fallback delete failed:", fallbackError);
            }
            showError(error.response?.data?.message || "Failed to remove WhatsApp");
        } finally {
            setSubmitting(false);
        }
    };

    const handleOAuthInstall = async () => {
        if (!formData.targetDomain.trim()) {
            showError("Please enter your Deskpro domain");
            return;
        }

        try {
            setSubmitting(true);
            let cleanDomain = formData.targetDomain.trim();
            cleanDomain = cleanDomain.replace(/^https?:\/\//i, '').split('/')[0].split('?')[0].split('#')[0];

            const params: any = {
                target_domain: cleanDomain
            };
            if (level && level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }

            showSuccess("Initiating Deskpro OAuth authorization...");
            const response = await integrationapi.get("integrations/rundesk/admin-consent-url", { params });

            if (response.data?.status && response.data?.admin_consent_url) {
                window.location.href = response.data.admin_consent_url;
            } else {
                showError(response.data?.message || "Failed to get consent URL for Deskpro");
            }
        } catch (error: any) {
            console.error("Deskpro OAuth install error:", error);
            showError(error.response?.data?.message || "Failed to initiate Deskpro OAuth authorization");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (authMethod === 'oauth') {
            await handleOAuthInstall();
            return;
        }

        const params: any = {
            name: "Rundesk",
            integration_type: "rundesk",
            integration_data: {
                api_url: formData.apiUrl,
                api_key: formData.apiKey,
                api_secret: formData.apiSecret,
                auth_method: 'manual'
            },
            is_enabled: true
        };
        if (level && level.name !== 'global' && level.id) {
            params.client_id = level.id;
        }

        try {
            setSubmitting(true);
            const payload: any = {
                api_url: formData.apiUrl,
                api_key: formData.apiKey,
                api_secret: formData.apiSecret,
                auth_method: 'manual',
                name: "rundeskpro"
            };
            if (level && level.name !== 'global' && level.id) {
                payload.client_id = level.id;
            }
            const response = await integrationapi.post("integrations/rundesk/connect", payload);

            if (response.data.status) {
                showSuccess(isEditing ? "Updated successfully" : "Connected successfully");
                setIsConfigModalOpen(false);
                fetchIntegrations();
            }
        } catch (error: any) {
            console.error("Connect integrationapi failed, trying fallback...", error);
            try {
                const response = isEditing
                    ? await baseapi.put("/ticketing/integration", params)
                    : await baseapi.post("/ticketing/integration", params);

                if (response.data.status) {
                    showSuccess(isEditing ? "Updated successfully" : "Connected successfully");
                    setIsConfigModalOpen(false);
                    fetchIntegrations();
                    return;
                }
            } catch (fallbackError: any) {
                console.error("Fallback connect failed:", fallbackError);
            }
            showError(error.response?.data?.message || "An error occurred");
        } finally {
            setSubmitting(false);
        }
    };

    const handleForceSync = async () => {
        try {
            setSyncing(true);
            const params: any = { refresh: true };
            if (level && level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await baseapi.get("/ticketing/metadata", { params });

            if (response.data.status) {
                showSuccess("Sync completed successfully");
            } else {
                showError("Sync failed to complete");
            }
        } catch (error: any) {
            showError(error.response?.data?.message || "Failed to sync metadata");
        } finally {
            setSyncing(false);
        }
    };

    if (loading && integrations.length === 0) {
        return <div className="p-20 flex justify-center"><Loader2 className="animate-spin text-orange-500" /></div>;
    }

    return (
        <div className="w-full p-2">
            <div className="flex items-center gap-1.5 mb-4">
                <div className="bg-orange-600 p-1.5 rounded shadow-lg">
                    <Workflow className="w-4 h-4 text-white" />
                </div>
                <div>
                    <h1 className="font-bold text-lg text-gray-200 leading-none">Integrations</h1>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* ─── RunDeskPro ─── */}
                <div className="bg-slate-800/60 border border-slate-700 hover:border-slate-700 rounded-2xl p-6 flex flex-col h-full shadow-lg transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12">
                            <img src="/rdp_logo.png" alt="RDP" className={`w-full h-full object-contain`} />
                        </div>
                        <div className="flex items-center gap-2">
                            {existingDeskpro && (
                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium tracking-wide">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Installed
                                </div>
                            )}
                            <button
                                onClick={() => handleOpenDocs('rundeskpro')}
                                title="View Documentation"
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 hover:text-orange-300 border border-orange-500/20 hover:border-orange-500/40 text-xs font-medium transition-all"
                            >
                                <BookOpen className="w-3.5 h-3.5" />
                                <span>Docs</span>
                            </button>
                        </div>
                    </div>

                    <h2 className="text-[16px] font-semibold text-white mb-2 leading-tight">RunDeskPro</h2>
                    <p className="text-[13px] text-gray-200 mb-6 flex-grow leading-relaxed">Helpdesk integration.</p>

                    <div className='w-full mt-auto pt-5 border-t border-slate-700/80 flex flex-col gap-2.5'>
                        {level && level.name === 'global' && (
                            <button
                                disabled={!existingDeskpro}
                                onClick={() => handleOpenManageClients(existingDeskproRaw)}
                                className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl text-[13px] font-medium transition-all border
                                    ${existingDeskpro
                                        ? 'bg-slate-800/40 border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600'
                                        : 'bg-[#111827]/40 border-slate-800 text-slate-600 cursor-not-allowed opacity-50'}`}
                            >
                                <Users className="w-4 h-4" />
                                Manage clients
                            </button>
                        )}
                        <div className="flex gap-3 w-full">
                            {existingDeskpro ? (
                                <>
                                    <button onClick={handleOpenEdit}
                                        className='flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-[14px] font-medium transition-all'>
                                        <Settings className="w-4 h-4" />
                                        Settings
                                    </button>

                                    {canDelete && isIntegrationEditable(existingDeskproRaw) && (
                                        <button
                                            onClick={() => setIsDeleteModalOpen(true)}
                                            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-transparent border border-transparent text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-xl text-[14px] font-medium transition-all"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Uninstall
                                        </button>
                                    )}
                                </>
                            ) : (
                                canCreate && (
                                    <button
                                        onClick={() => { setFormData({ apiUrl: '', apiKey: '', apiSecret: '', targetDomain: '' }); setIsEditing(false); setIsConfigModalOpen(true); }}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-[14px] font-medium transition-all"
                                    >
                                        <Download className="w-4 h-4" />
                                        Install
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                </div>

                {/* ─── External API ─── */}
                <div className="bg-slate-800/60 border border-slate-700 hover:border-slate-700 rounded-2xl p-6 flex flex-col h-full shadow-lg transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center">
                            <Cpu className="text-orange-500" size={24} />
                        </div>
                        <div className="flex items-center gap-2">
                            {externalApiIntegration && (
                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium tracking-wide">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Installed
                                </div>
                            )}
                            <button
                                onClick={() => handleOpenDocs('external_api')}
                                title="View Documentation"
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 hover:text-orange-300 border border-orange-500/20 hover:border-orange-500/40 text-xs font-medium transition-all"
                            >
                                <BookOpen className="w-3.5 h-3.5" />
                                <span>Docs</span>
                            </button>
                        </div>
                    </div>

                    <h2 className="text-[16px] font-semibold text-white mb-2 leading-tight">External API</h2>
                    <p className="text-[13px] text-gray-200 mb-6 flex-grow leading-relaxed">Connect external services via secure tokens.</p>

                    <div className='w-full mt-auto pt-5 border-t border-slate-700/80 flex flex-col gap-2.5'>
                        {level && level.name === 'global' && (
                            <button
                                disabled={!externalApiIntegration}
                                onClick={() => handleOpenManageClients(externalApiIntegration)}
                                className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl text-[13px] font-medium transition-all border
                                    ${externalApiIntegration
                                        ? 'bg-slate-800/40 border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600'
                                        : 'bg-[#111827]/40 border-slate-800 text-slate-600 cursor-not-allowed opacity-50'}`}
                            >
                                <Users className="w-4 h-4" />
                                Manage clients
                            </button>
                        )}
                        <div className="flex gap-3 w-full">
                            {externalApiIntegration ? (
                                <>
                                    <button
                                        onClick={() => setIsExternalApiSettingsOpen(true)}
                                        className='flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-[14px] font-medium transition-all'
                                    >
                                        <Settings className="w-4 h-4" />
                                        Settings
                                    </button>
                                    {canDelete && isIntegrationEditable(externalApiIntegration) && (
                                        <button
                                            onClick={() => setIsExternalApiDeleteModalOpen(true)}
                                            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-transparent border border-transparent text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-xl text-[14px] font-medium transition-all"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Uninstall
                                        </button>
                                    )}
                                </>
                            ) : (
                                canCreate && (
                                    <button
                                        onClick={() => setIsExternalApiModalOpen(true)}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-[14px] font-medium transition-all"
                                    >
                                        <Download className="w-4 h-4" />
                                        Install
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                </div>

                {/* ─── Microsoft Teams ─── */}
                <div className="bg-slate-800/60 border border-slate-700 hover:border-slate-700 rounded-2xl p-6 flex flex-col h-full shadow-lg transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12">
                            <img src="/teams.svg" alt="Teams" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex items-center gap-2">
                            {existingTeams && (
                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium tracking-wide">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Installed
                                </div>
                            )}
                            <button
                                onClick={() => handleOpenDocs('teams')}
                                title="View Documentation"
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 hover:text-orange-300 border border-orange-500/20 hover:border-orange-500/40 text-xs font-medium transition-all"
                            >
                                <BookOpen className="w-3.5 h-3.5" />
                                <span>Docs</span>
                            </button>
                        </div>
                    </div>
                    <h2 className="text-[16px] font-semibold text-white mb-2 leading-tight">Microsoft Teams</h2>
                    <p className="text-[13px] text-gray-200 mb-6 flex-grow leading-relaxed">Organization-wide Teams integration.</p>
                    <div className='w-full mt-auto pt-5 border-t border-slate-700/80 flex flex-col gap-2.5'>
                        {level && level.name === 'global' && (
                            <button
                                disabled={!existingTeams}
                                onClick={() => handleOpenManageClients(existingTeams)}
                                className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl text-[13px] font-medium transition-all border
                                    ${existingTeams
                                        ? 'bg-slate-800/40 border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600'
                                        : 'bg-[#111827]/40 border-slate-800 text-slate-600 cursor-not-allowed opacity-50'}`}
                            >
                                <Users className="w-4 h-4" />
                                Manage clients
                            </button>
                        )}
                        <div className="flex gap-3 w-full">
                            {existingTeams ? (
                                canDelete && isIntegrationEditable(existingTeams) && (
                                    <button
                                        onClick={() => handleUninstall('teams', existingTeams.id)}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-transparent border border-red-500/30 text-red-500 hover:bg-red-500/10 rounded-xl text-[14px] font-medium transition-all"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Uninstall
                                    </button>
                                )
                            ) : (
                                canCreate && (
                                    <button
                                        onClick={() => handleInstall('teams')}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-850 hover:bg-slate-700 text-white rounded-xl text-[14px] font-medium transition-all cursor-pointer border border-slate-700 hover:border-slate-600 shadow-md"
                                    >
                                        <Download className="w-4 h-4" />
                                        Install
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                </div>

                {/* ─── Microsoft SSO ─── */}
                <div className="bg-slate-800/60 border border-slate-700 hover:border-slate-700 rounded-2xl p-6 flex flex-col h-full shadow-lg transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12">
                            <img src="/microsoftgraph.svg" alt="Microsoft SSO" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex items-center gap-2">
                            {existingSso && (
                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium tracking-wide">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Installed
                                </div>
                            )}
                            <button
                                onClick={() => handleOpenDocs('microsoft-sso')}
                                title="View Documentation"
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 hover:text-orange-300 border border-orange-500/20 hover:border-orange-500/40 text-xs font-medium transition-all"
                            >
                                <BookOpen className="w-3.5 h-3.5" />
                                <span>Docs</span>
                            </button>
                        </div>
                    </div>
                    <h2 className="text-[16px] font-semibold text-white mb-2 leading-tight">Microsoft SSO</h2>
                    <p className="text-[13px] text-gray-200 mb-6 flex-grow leading-relaxed">Enable Single Sign-On with Microsoft for your organization.</p>
                    <div className='w-full mt-auto pt-5 border-t border-slate-700/80 flex flex-col gap-2.5'>
                        {level && level.name === 'global' && (
                            <button
                                disabled={!existingSso}
                                onClick={() => handleOpenManageClients(existingSso)}
                                className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl text-[13px] font-medium transition-all border
                                    ${existingSso
                                        ? 'bg-slate-800/40 border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600'
                                        : 'bg-[#111827]/40 border-slate-800 text-slate-600 cursor-not-allowed opacity-50'}`}
                            >
                                <Users className="w-4 h-4" />
                                Manage clients
                            </button>
                        )}
                        <div className="flex gap-3 w-full">
                            {existingSso ? (
                                canDelete && isIntegrationEditable(existingSso) && (
                                    <button
                                        onClick={() => handleUninstall('microsoft-sso', existingSso.id)}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-transparent border border-red-500/30 text-red-500 hover:bg-red-500/10 rounded-xl text-[14px] font-medium transition-all"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Uninstall
                                    </button>
                                )
                            ) : (
                                canCreate && (
                                    <button
                                        onClick={() => handleInstall('microsoft-sso')}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-850 hover:bg-slate-700 text-white rounded-xl text-[14px] font-medium transition-all cursor-pointer border border-slate-700 hover:border-slate-600 shadow-md"
                                    >
                                        <Download className="w-4 h-4" />
                                        Install
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                </div>

                {/* ─── Google SSO ─── */}
                <div className="bg-slate-800/60 border border-slate-700 hover:border-slate-700 rounded-2xl p-6 flex flex-col h-full shadow-lg transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12">
                            <img src="/google.svg" alt="Google SSO" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex items-center gap-2">
                            {existingGoogleSso && (
                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium tracking-wide">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Installed
                                </div>
                            )}
                            <button
                                onClick={() => handleOpenDocs('google-sso')}
                                title="View Documentation"
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 hover:text-orange-300 border border-orange-500/20 hover:border-orange-500/40 text-xs font-medium transition-all"
                            >
                                <BookOpen className="w-3.5 h-3.5" />
                                <span>Docs</span>
                            </button>
                        </div>
                    </div>
                    <h2 className="text-[16px] font-semibold text-white mb-2 leading-tight">Google SSO</h2>
                    <p className="text-[13px] text-gray-200 mb-6 flex-grow leading-relaxed">Enable Single Sign-On with Google for your organization.</p>
                    <div className='w-full mt-auto pt-5 border-t border-slate-700/80 flex flex-col gap-2.5'>
                        {level && level.name === 'global' && (
                            <button
                                disabled={!existingGoogleSso}
                                onClick={() => handleOpenManageClients(existingGoogleSso)}
                                className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl text-[13px] font-medium transition-all border
                                    ${existingGoogleSso
                                        ? 'bg-slate-800/40 border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600'
                                        : 'bg-[#111827]/40 border-slate-800 text-slate-600 cursor-not-allowed opacity-50'}`}
                            >
                                <Users className="w-4 h-4" />
                                Manage clients
                            </button>
                        )}
                        <div className="flex gap-3 w-full">
                            {existingGoogleSso ? (
                                canDelete && isIntegrationEditable(existingGoogleSso) && (
                                    <button
                                        onClick={() => handleUninstall('google-sso', existingGoogleSso.id)}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-transparent border border-red-500/30 text-red-500 hover:bg-red-500/10 rounded-xl text-[14px] font-medium transition-all"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Uninstall
                                    </button>
                                )
                            ) : (
                                canCreate && (
                                    <button
                                        onClick={() => handleInstall('google-sso')}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-850 hover:bg-slate-700 text-white rounded-xl text-[14px] font-medium transition-all cursor-pointer border border-slate-700 hover:border-slate-600 shadow-md"
                                    >
                                        <Download className="w-4 h-4" />
                                        Install
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                </div>

                {/* ─── Slack ─── */}
                <div className="bg-slate-800/60 border border-slate-700 hover:border-slate-700 rounded-2xl p-6 flex flex-col h-full shadow-lg transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12">
                            <img src="/slack.svg" alt="Slack" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex items-center gap-2">
                            {existingSlack && (
                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium tracking-wide">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Installed
                                </div>
                            )}
                            <button
                                onClick={() => handleOpenDocs('slack')}
                                title="View Documentation"
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 hover:text-orange-300 border border-orange-500/20 hover:border-orange-500/40 text-xs font-medium transition-all"
                            >
                                <BookOpen className="w-3.5 h-3.5" />
                                <span>Docs</span>
                            </button>
                        </div>
                    </div>
                    <h2 className="text-[16px] font-semibold text-white mb-2 leading-tight">Slack</h2>
                    <p className="text-[13px] text-gray-200 mb-6 flex-grow leading-relaxed">Slack workspace integration.</p>
                    <div className='w-full mt-auto pt-5 border-t border-slate-700/80 flex flex-col gap-2.5'>
                        {level && level.name === 'global' && (
                            <button
                                disabled={!existingSlack}
                                onClick={() => handleOpenManageClients(existingSlack)}
                                className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl text-[13px] font-medium transition-all border
                                    ${existingSlack
                                        ? 'bg-slate-800/40 border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600'
                                        : 'bg-[#111827]/40 border-slate-800 text-slate-600 cursor-not-allowed opacity-50'}`}
                            >
                                <Users className="w-4 h-4" />
                                Manage clients
                            </button>
                        )}
                        <div className="flex gap-3 w-full">
                            {existingSlack ? (
                                canDelete && isIntegrationEditable(existingSlack) && (
                                    <button
                                        onClick={() => handleUninstall('slack', existingSlack.id)}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-transparent border border-red-500/30 text-red-500 hover:bg-red-500/10 rounded-xl text-[14px] font-medium transition-all"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Uninstall
                                    </button>
                                )
                            ) : (
                                canCreate && (
                                    <button
                                        onClick={() => handleInstall('slack')}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-850 hover:bg-slate-700 text-white rounded-xl text-[14px] font-medium transition-all cursor-pointer border border-slate-700 hover:border-slate-600 shadow-md"
                                    >
                                        <Download className="w-4 h-4" />
                                        Install
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                </div>

                {/* ─── Google Workspace ─── */}
                <div className="bg-slate-800/60 border border-slate-700 hover:border-slate-700 rounded-2xl p-6 flex flex-col h-full shadow-lg transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12">
                            <img src="/google.svg" alt="Google Workspace" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex items-center gap-2">
                            {existingGoogleWorkspace && (
                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium tracking-wide">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Installed
                                </div>
                            )}
                            <button
                                onClick={() => handleOpenDocs('google-workspace')}
                                title="View Documentation"
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 hover:text-orange-300 border border-orange-500/20 hover:border-orange-500/40 text-xs font-medium transition-all"
                            >
                                <BookOpen className="w-3.5 h-3.5" />
                                <span>Docs</span>
                            </button>
                        </div>
                    </div>
                    <h2 className="text-[16px] font-semibold text-white mb-2 leading-tight">Google Workspace</h2>
                    <p className="text-[13px] text-gray-200 mb-6 flex-grow leading-relaxed">Enable user provisioning/directory synchronization for your organization.</p>
                    <div className='w-full mt-auto pt-5 border-t border-slate-700/80 flex flex-col gap-2.5'>
                        {level && level.name === 'global' && (
                            <button
                                disabled={!existingGoogleWorkspace}
                                onClick={() => handleOpenManageClients(existingGoogleWorkspace)}
                                className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl text-[13px] font-medium transition-all border
                                    ${existingGoogleWorkspace
                                        ? 'bg-slate-800/40 border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600'
                                        : 'bg-[#111827]/40 border-slate-800 text-slate-600 cursor-not-allowed opacity-50'}`}
                            >
                                <Users className="w-4 h-4" />
                                Manage clients
                            </button>
                        )}
                        <div className="flex gap-3 w-full">
                            {existingGoogleWorkspace ? (
                                canDelete && isIntegrationEditable(existingGoogleWorkspace) && (
                                    <button
                                        onClick={() => handleUninstall('google-workspace', existingGoogleWorkspace.id)}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-transparent border border-red-500/30 text-red-500 hover:bg-red-500/10 rounded-xl text-[14px] font-medium transition-all"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Uninstall
                                    </button>
                                )
                            ) : (
                                canCreate && (
                                    <button
                                        onClick={() => handleInstall('google-workspace')}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-850 hover:bg-slate-700 text-white rounded-xl text-[14px] font-medium transition-all cursor-pointer border border-slate-700 hover:border-slate-600 shadow-md"
                                    >
                                        <Download className="w-4 h-4" />
                                        Install
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                </div>

                {/* ─── Webhook ─── */}
                <div className="bg-slate-800/60 border border-slate-700 hover:border-slate-700 rounded-2xl p-6 flex flex-col h-full shadow-lg transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12">
                            <img src="/webhook.svg" alt="Webhook" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex items-center gap-2">
                            {existingWebhook && (
                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium tracking-wide">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Installed
                                </div>
                            )}
                            <button
                                onClick={() => handleOpenDocs('webhook')}
                                title="View Documentation"
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 hover:text-orange-300 border border-orange-500/20 hover:border-orange-500/40 text-xs font-medium transition-all"
                            >
                                <BookOpen className="w-3.5 h-3.5" />
                                <span>Docs</span>
                            </button>
                        </div>
                    </div>
                    <h2 className="text-[16px] font-semibold text-white mb-2 leading-tight">Webhook</h2>
                    <p className="text-[13px] text-gray-200 mb-6 flex-grow leading-relaxed">Webhook integration</p>
                    <div className='w-full mt-auto pt-5 border-t border-slate-700/80 flex flex-col gap-2.5'>
                        {level && level.name === 'global' && (
                            <button
                                disabled={!existingWebhook}
                                onClick={() => handleOpenManageClients(existingWebhook)}
                                className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl text-[13px] font-medium transition-all border
                                    ${existingWebhook
                                        ? 'bg-slate-800/40 border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600'
                                        : 'bg-[#111827]/40 border-slate-800 text-slate-600 cursor-not-allowed opacity-50'}`}
                            >
                                <Users className="w-4 h-4" />
                                Manage clients
                            </button>
                        )}
                        <div className="flex gap-3 w-full">
                            {existingWebhook ? (
                                <>
                                    <button onClick={() => setIsWebhookModalOpen(true)}
                                        className='flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-[14px] font-medium transition-all'>
                                        <Settings className="w-4 h-4" />
                                        Settings
                                    </button>
                                    {canDelete && isIntegrationEditable(existingWebhook) && (
                                        <button
                                            onClick={() => setIsWebhookDeleteModalOpen(true)}
                                            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-transparent border border-transparent text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-xl text-[14px] font-medium transition-all"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Uninstall
                                        </button>
                                    )}
                                </>
                            ) : (
                                canCreate && (
                                    <button onClick={() => setIsWebhookModalOpen(true)} className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-[14px] font-medium transition-all">
                                        <Download className="w-4 h-4" />
                                        Install
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                </div>

                {/* ─── Email ─── */}
                <div className="bg-slate-800/60 border border-slate-700 hover:border-slate-700 rounded-2xl p-6 flex flex-col h-full shadow-lg transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12">
                            <img src="/email.png" alt="Email" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex items-center gap-2">
                            {existingEmail && (
                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium tracking-wide">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Installed
                                </div>
                            )}
                            <button
                                onClick={() => handleOpenDocs('smtp')}
                                title="View Documentation"
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 hover:text-orange-300 border border-orange-500/20 hover:border-orange-500/40 text-xs font-medium transition-all"
                            >
                                <BookOpen className="w-3.5 h-3.5" />
                                <span>Docs</span>
                            </button>
                        </div>
                    </div>
                    <h2 className="text-[16px] font-semibold text-white mb-2 leading-tight">Email</h2>
                    <p className="text-[13px] text-gray-200 mb-6 flex-grow leading-relaxed">Email integration</p>
                    <div className='w-full mt-auto pt-5 border-t border-slate-700/80 flex flex-col gap-2.5'>
                        {level && level.name === 'global' && (
                            <button
                                disabled={!existingEmail}
                                onClick={() => handleOpenManageClients(existingEmail)}
                                className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl text-[13px] font-medium transition-all border
                                    ${existingEmail
                                        ? 'bg-slate-800/40 border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600'
                                        : 'bg-[#111827]/40 border-slate-800 text-slate-600 cursor-not-allowed opacity-50'}`}
                            >
                                <Users className="w-4 h-4" />
                                Manage clients
                            </button>
                        )}
                        <div className="flex gap-3 w-full">
                            {existingEmail ? (
                                <>
                                    <button onClick={() => setIsEmailModalOpen(true)}
                                        className='flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-[14px] font-medium transition-all'>
                                        <Settings className="w-4 h-4" />
                                        Settings
                                    </button>
                                    {canDelete && isIntegrationEditable(existingEmail) && (
                                        <button
                                            onClick={() => setIsEmailDeleteModalOpen(true)}
                                            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-transparent border border-transparent text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-xl text-[14px] font-medium transition-all"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Uninstall
                                        </button>
                                    )}
                                </>
                            ) : (
                                canCreate && (
                                    <button onClick={() => setIsEmailModalOpen(true)} className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-[14px] font-medium transition-all">
                                        <Download className="w-4 h-4" />
                                        Install
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                </div>

                {/* ─── WhatsApp ─── */}
                <div className="bg-slate-800/60 border border-slate-700 hover:border-slate-700 rounded-2xl p-6 flex flex-col h-full shadow-lg transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12">
                            <img src="/whatsapp.png" alt="WhatsApp" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex items-center gap-2">
                            {existingWhatsapp && (
                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium tracking-wide">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Installed
                                </div>
                            )}
                            <button
                                onClick={() => handleOpenDocs('whatsapp')}
                                title="View Documentation"
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 hover:text-orange-300 border border-orange-500/20 hover:border-orange-500/40 text-xs font-medium transition-all"
                            >
                                <BookOpen className="w-3.5 h-3.5" />
                                <span>Docs</span>
                            </button>
                        </div>
                    </div>
                    <h2 className="text-[16px] font-semibold text-white mb-2 leading-tight">WhatsApp</h2>
                    <p className="text-[13px] text-gray-200 mb-6 flex-grow leading-relaxed">WhatsApp Business API integration.</p>
                    <div className='w-full mt-auto pt-5 border-t border-slate-700/80 flex flex-col gap-2.5'>
                        {level && level.name === 'global' && (
                            <button
                                disabled={!existingWhatsapp}
                                onClick={() => handleOpenManageClients(existingWhatsapp)}
                                className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl text-[13px] font-medium transition-all border
                                    ${existingWhatsapp
                                        ? 'bg-slate-800/40 border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-600'
                                        : 'bg-[#111827]/40 border-slate-800 text-slate-600 cursor-not-allowed opacity-50'}`}
                            >
                                <Users className="w-4 h-4" />
                                Manage clients
                            </button>
                        )}
                        <div className="flex gap-3 w-full">
                            {existingWhatsapp ? (
                                <>
                                    <button onClick={() => setIsWhatsappModalOpen(true)}
                                        className='flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-[14px] font-medium transition-all'>
                                        <Settings className="w-4 h-4" />
                                        Settings
                                    </button>
                                    {canDelete && isIntegrationEditable(existingWhatsapp) && (
                                        <button
                                            onClick={() => setIsWhatsappDeleteModalOpen(true)}
                                            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-transparent border border-transparent text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-xl text-[14px] font-medium transition-all"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Uninstall
                                        </button>
                                    )}
                                </>
                            ) : (
                                canCreate && (
                                    <button onClick={() => setIsWhatsappModalOpen(true)} className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-[14px] font-medium transition-all">
                                        <Download className="w-4 h-4" />
                                        Install
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                </div>

            </div>

            {/* ─── Configuration Modal (Add/Edit) ─── */}
            {isConfigModalOpen && (() => {
                const isDeskproEditable = isIntegrationEditable(existingDeskproRaw);
                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                        <div className="bg-[#111827] border border-gray-700/50 rounded-2xl w-full max-w-xl shadow-2xl animate-in zoom-in duration-300">
                            <div className="px-6 py-5 border-b border-gray-800 flex items-center justify-between">
                                <h2 className="text-lg font-bold text-white">{isEditing ? (isDeskproEditable ? 'Update Credentials' : 'RunDeskPro Settings') : 'Configure RunDeskPro'}</h2>
                                <button onClick={() => setIsConfigModalOpen(false)} className="text-gray-500 hover:text-white p-1 hover:bg-gray-800 rounded-full transition-colors"><X className="w-5 h-5" /></button>
                            </div>

                            <form onSubmit={handleSubmit} className="p-8 space-y-6">
                                {!isDeskproEditable && (
                                    <div className="p-3.5 bg-blue-500/10 border border-blue-500/20 rounded-xl text-blue-400 text-xs flex items-center gap-2.5">
                                        <Globe className="w-4 h-4 shrink-0" />
                                        <span>This global integration is managed centrally. Settings are read-only.</span>
                                    </div>
                                )}

                                {/* Authentication Method Choice */}
                                <div>
                                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-2 px-1">
                                        Authentication Method
                                    </label>
                                    <div className="grid grid-cols-2 gap-3 p-1.5 bg-[#0D1117] border border-gray-800 rounded-xl">
                                        <button
                                            type="button"
                                            disabled={!isDeskproEditable}
                                            onClick={() => setAuthMethod('manual')}
                                            className={`py-2.5 px-4 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-2 ${authMethod === 'manual'
                                                    ? 'bg-slate-800 text-orange-400 border border-slate-700 shadow-sm'
                                                    : 'text-gray-400 hover:text-gray-200 hover:bg-slate-800/40'
                                                } ${!isDeskproEditable ? 'cursor-not-allowed opacity-80' : ''}`}
                                        >
                                            <Key className="w-3.5 h-3.5" />
                                            Manual Credentials
                                        </button>
                                        <button
                                            type="button"
                                            disabled={!isDeskproEditable}
                                            onClick={() => setAuthMethod('oauth')}
                                            className={`py-2.5 px-4 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-2 ${authMethod === 'oauth'
                                                    ? 'bg-slate-800 text-orange-400 border border-slate-700 shadow-sm'
                                                    : 'text-gray-400 hover:text-gray-200 hover:bg-slate-800/40'
                                                } ${!isDeskproEditable ? 'cursor-not-allowed opacity-80' : ''}`}
                                        >
                                            <Globe className="w-3.5 h-3.5" />
                                            OAuth 2.0
                                        </button>
                                    </div>
                                </div>

                                {authMethod === 'manual' ? (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-2 px-1">Base API URL</label>
                                            <div className="relative group">
                                                <Globe className="absolute left-3 top-3.5 h-4 w-4 text-gray-600 group-focus-within:text-orange-500 transition-colors" />
                                                <input name="apiUrl" type="url" disabled={!isDeskproEditable} value={formData.apiUrl} onChange={(e) => setFormData({ ...formData, apiUrl: e.target.value })} className="w-full bg-[#0D1117] border border-gray-800 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-200 focus:border-orange-500/50 outline-none transition-all disabled:opacity-70 disabled:cursor-not-allowed" placeholder="https://api.example.com/v1" />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="group">
                                                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-2 px-1">API Key</label>
                                                <input name="apiKey" disabled={!isDeskproEditable} value={formData.apiKey} onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })} autoComplete="one-time-code" className="w-full bg-[#0D1117] border border-gray-800 rounded-xl px-4 py-3 text-sm text-gray-200 focus:border-orange-500/50 outline-none transition-all disabled:opacity-70 disabled:cursor-not-allowed" />
                                            </div>
                                            <div className="group">
                                                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-2 px-1">API Secret</label>
                                                <input name="apiSecret" type="password" disabled={!isDeskproEditable} value={formData.apiSecret} onChange={(e) => setFormData({ ...formData, apiSecret: e.target.value })} autoComplete="new-password" placeholder="••••••••" className="w-full bg-[#0D1117] border border-gray-800 rounded-xl px-4 py-3 text-sm text-gray-200 focus:border-orange-500/50 outline-none transition-all disabled:opacity-70 disabled:cursor-not-allowed" />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-2 px-1">
                                                Deskpro Target Domain
                                            </label>
                                            <div className="relative group">
                                                <Globe className="absolute left-3 top-3.5 h-4 w-4 text-gray-600 group-focus-within:text-orange-500 transition-colors" />
                                                <input
                                                    name="targetDomain"
                                                    type="text"
                                                    disabled={!isDeskproEditable}
                                                    value={formData.targetDomain}
                                                    onChange={(e) => setFormData({ ...formData, targetDomain: e.target.value })}
                                                    className="w-full bg-[#0D1117] border border-gray-800 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-200 focus:border-orange-500/50 outline-none transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                                    placeholder="deskpro.example.com"
                                                />
                                            </div>
                                            <p className="text-[11px] text-gray-500 mt-1.5 px-1">
                                                Enter your Deskpro instance domain (e.g. deskpro.example.com or https://deskpro.example.com)
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Fixed Action Button Layout */}
                                <div className="pt-4 flex justify-between items-center gap-4 w-full">
                                    {!isDeskproEditable ? (
                                        <button type="button" onClick={() => setIsConfigModalOpen(false)} className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-sm font-bold transition-all ml-auto">
                                            Close
                                        </button>
                                    ) : (
                                        <>
                                            <button type="button" onClick={() => setIsConfigModalOpen(false)} className="px-6 py-3 border border-gray-700 rounded-xl text-sm font-bold text-gray-400 hover:bg-gray-800 transition-all">
                                                Cancel
                                            </button>

                                            {authMethod === 'manual' ? (
                                                <div className="flex items-center gap-3">
                                                    {isEditing && (
                                                        <button
                                                            type="button"
                                                            onClick={handleForceSync}
                                                            disabled={syncing || submitting}
                                                            className="px-6 py-3 border border-orange-500/50 text-orange-500 rounded-xl text-sm font-bold hover:bg-orange-500/10 transition-all shadow-sm shadow-orange-900/10 disabled:opacity-50 flex justify-center items-center gap-2"
                                                        >
                                                            <RotateCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                                                            Force Sync
                                                        </button>
                                                    )}

                                                    <button type="submit" disabled={submitting || syncing} className="px-6 py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-xl text-sm font-bold shadow-lg shadow-orange-900/20 disabled:opacity-50 transition-all flex justify-center items-center gap-2">
                                                        {submitting ? <Loader2 className="animate-spin w-4 h-4" /> : <Save className="w-4 h-4" />}
                                                        {isEditing ? 'Save Changes' : 'Connect Service'}
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={handleOAuthInstall}
                                                    disabled={submitting}
                                                    className="px-6 py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-xl text-sm font-bold shadow-lg shadow-orange-900/20 disabled:opacity-50 transition-all flex justify-center items-center gap-2"
                                                >
                                                    {submitting ? <Loader2 className="animate-spin w-4 h-4" /> : <Workflow className="w-4 h-4" />}
                                                    Authorize with RunDeskPro
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                );
            })()}

            {/* ─── Delete Confirmation Modal ─── */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
                    <div className="bg-[#111827] border border-red-900/20 rounded-2xl w-full max-w-sm shadow-2xl animate-in zoom-in duration-200 overflow-hidden">
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <AlertTriangle className="w-8 h-8 text-red-500" />
                            </div>
                            <h2 className="text-xl font-bold text-white mb-2">Disconnect Integration?</h2>
                            <p className="text-sm text-gray-400 leading-relaxed">
                                Are you sure you want to remove <span className="text-white font-medium">RunDeskPro</span>? This will stop all synchronization immediately.
                            </p>
                        </div>
                        <div className="px-8 py-6 bg-gray-900/50 border-t border-gray-800 flex gap-3">
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="flex-1 py-3 border border-gray-700 rounded-xl text-sm font-bold text-gray-400 hover:bg-gray-800 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={submitting}
                                className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-red-900/20 flex justify-center items-center gap-2"
                            >
                                {submitting ? <Loader2 className="animate-spin w-4 h-4" /> : 'Yes, Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── External API Delete Confirmation Modal ─── */}
            {isExternalApiDeleteModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
                    <div className="bg-[#111827] border border-red-900/20 rounded-2xl w-full max-w-sm shadow-2xl animate-in zoom-in duration-200 overflow-hidden">
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <AlertTriangle className="w-8 h-8 text-red-500" />
                            </div>
                            <h2 className="text-xl font-bold text-white mb-2">Disconnect API?</h2>
                            <p className="text-sm text-gray-400 leading-relaxed">
                                Are you sure you want to remove <span className="text-white font-medium">External API</span>? All services using these credentials will lose access.
                            </p>
                        </div>
                        <div className="px-8 py-6 bg-gray-900/50 border-t border-gray-800 flex gap-3">
                            <button
                                onClick={() => setIsExternalApiDeleteModalOpen(false)}
                                className="flex-1 py-3 border border-gray-700 rounded-xl text-sm font-bold text-gray-400 hover:bg-gray-800 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteExternalApi}
                                disabled={submitting}
                                className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-red-900/20 flex justify-center items-center gap-2"
                            >
                                {submitting ? <Loader2 className="animate-spin w-4 h-4" /> : 'Yes, Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Webhook Delete Confirmation Modal ─── */}
            {isWebhookDeleteModalOpen && existingWebhook && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
                    <div className="bg-[#111827] border border-red-900/20 rounded-2xl w-full max-w-sm shadow-2xl animate-in zoom-in duration-200 overflow-hidden">
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <AlertTriangle className="w-8 h-8 text-red-500" />
                            </div>
                            <h2 className="text-xl font-bold text-white mb-2">Disconnect Webhook?</h2>
                            <p className="text-sm text-gray-400 leading-relaxed">
                                Are you sure you want to remove Webhook integration?
                            </p>
                        </div>
                        <div className="px-8 py-6 bg-gray-900/50 border-t border-gray-800 flex gap-3">
                            <button
                                onClick={() => setIsWebhookDeleteModalOpen(false)}
                                className="flex-1 py-3 border border-gray-700 rounded-xl text-sm font-bold text-gray-400 hover:bg-gray-800 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteWebhook}
                                disabled={submitting}
                                className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-red-900/20 flex justify-center items-center gap-2"
                            >
                                {submitting ? <Loader2 className="animate-spin w-4 h-4" /> : 'Yes, Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Email Delete Confirmation Modal ─── */}
            {isEmailDeleteModalOpen && existingEmail && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
                    <div className="bg-[#111827] border border-red-900/20 rounded-2xl w-full max-w-sm shadow-2xl animate-in zoom-in duration-200 overflow-hidden">
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <AlertTriangle className="w-8 h-8 text-red-500" />
                            </div>
                            <h2 className="text-xl font-bold text-white mb-2">Disconnect Email?</h2>
                            <p className="text-sm text-gray-400 leading-relaxed">
                                Are you sure you want to remove Email integration?
                            </p>
                        </div>
                        <div className="px-8 py-6 bg-gray-900/50 border-t border-gray-800 flex gap-3">
                            <button
                                onClick={() => setIsEmailDeleteModalOpen(false)}
                                className="flex-1 py-3 border border-gray-700 rounded-xl text-sm font-bold text-gray-400 hover:bg-gray-800 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteEmail}
                                disabled={submitting}
                                className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-red-900/20 flex justify-center items-center gap-2"
                            >
                                {submitting ? <Loader2 className="animate-spin w-4 h-4" /> : 'Yes, Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete WhatsApp Modal */}
            {isWhatsappDeleteModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
                    <div className="bg-[#111827] border border-gray-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6 text-center">
                            <div className="w-16 h-16 bg-red-500/10 border border-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <AlertTriangle className="w-8 h-8 text-red-500" />
                            </div>
                            <h2 className="text-xl font-bold text-white mb-2">Disconnect WhatsApp?</h2>
                            <p className="text-sm text-gray-400 leading-relaxed">
                                Are you sure you want to remove WhatsApp integration?
                            </p>
                        </div>
                        <div className="px-8 py-6 bg-gray-900/50 border-t border-gray-800 flex gap-3">
                            <button
                                onClick={() => setIsWhatsappDeleteModalOpen(false)}
                                className="flex-1 py-3 border border-gray-700 rounded-xl text-sm font-bold text-gray-400 hover:bg-gray-800 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteWhatsapp}
                                disabled={submitting}
                                className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-red-900/20 flex justify-center items-center gap-2"
                            >
                                {submitting ? <Loader2 className="animate-spin w-4 h-4" /> : 'Yes, Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ExternalApiModal
                isOpen={isExternalApiModalOpen}
                onClose={() => setIsExternalApiModalOpen(false)}
                onSuccess={() => fetchIntegrations()}
            />
            <ExternalApiSettings
                isOpen={isExternalApiSettingsOpen}
                onClose={() => setIsExternalApiSettingsOpen(false)}
                onUninstall={() => fetchIntegrations()}
                integrationData={externalApiIntegration ? {
                    ...externalApiIntegration,
                    editable: isIntegrationEditable(externalApiIntegration)
                } : undefined}
            />
            <WebhookIntegrationModal
                isOpen={isWebhookModalOpen}
                onClose={() => setIsWebhookModalOpen(false)}
                onSuccess={() => fetchIntegrations()}
                integrationData={existingWebhook ? {
                    ...existingWebhook,
                    editable: isIntegrationEditable(existingWebhook)
                } : undefined}
            />
            <EmailIntegrationModal
                isOpen={isEmailModalOpen}
                onClose={() => setIsEmailModalOpen(false)}
                onSuccess={() => fetchIntegrations()}
                integrationData={existingEmail ? {
                    ...existingEmail,
                    editable: isIntegrationEditable(existingEmail)
                } : undefined}
            />
            <WhatsappIntegrationModal
                isOpen={isWhatsappModalOpen}
                onClose={() => setIsWhatsappModalOpen(false)}
                onSuccess={() => fetchIntegrations()}
                integrationData={existingWhatsapp ? {
                    ...existingWhatsapp,
                    editable: isIntegrationEditable(existingWhatsapp)
                } : undefined}
            />
            {selectedIntegration && (
                <ManageClientsModal
                    isOpen={isManageClientsOpen}
                    onClose={() => {
                        setIsManageClientsOpen(false);
                        setSelectedIntegration(null);
                    }}
                    onSuccess={() => fetchIntegrations()}
                    integrationData={selectedIntegration}
                />
            )}
            <IntegrationDocumentationModal
                isOpen={isDocModalOpen}
                onClose={() => setIsDocModalOpen(false)}
                initialIntegrationId={selectedDocId}
                onOpenConfigure={handleConfigureFromDocs}
            />
        </div>
    );
}